import update from 'immutability-helper';

import pruneRuleHiddenTickets from './pruneRuleHiddenTickets';

/**
 * Returns a new checkout form with the ticket type's quantity set.
 * With `exclusive: true` every other ticket type in the same group is
 * deselected in the same update (single-selection groups).
 *
 * Deselection (quantity 0) also clears the entry's typed
 * payWhatYouWantAmount, so a re-selected ticket starts from the
 * placeholder rather than a stale amount.
 *
 * With `pageTicketGroups` (the page's ticket groups) every ticket a rule
 * hides after this change is dropped too, so its whole chain leaves the basket.
 */
export default function applyTicketTypeSelection(
  form,
  ticketGroupId,
  ticketTypeId,
  quantity = 1,
  { exclusive = false, pageTicketGroups } = {},
) {
  const entries = form.ticketGroups[ticketGroupId] || [];
  const exists = entries.some(t => t.ticketTypeId === ticketTypeId);

  const withQuantity = (entry, nextQuantity) => {
    if (nextQuantity === 0) {
      const { payWhatYouWantAmount, ...rest } = entry;
      return { ...rest, quantity: 0 };
    }
    return { ...entry, quantity: nextQuantity };
  };

  let nextEntries = entries.map(entry => {
    if (entry.ticketTypeId === ticketTypeId) return withQuantity(entry, quantity);
    if (exclusive) return withQuantity(entry, 0);
    return entry;
  });

  // Deselecting a ticket that was never selected is a no-op entry-wise —
  // don't append a ghost {quantity: 0} row (it would register as a form
  // change and fire a spurious draft update).
  if (!exists && quantity !== 0) {
    nextEntries = [...nextEntries, { ticketTypeId, quantity }];
  }

  const nextForm = update(form, {
    ticketGroups: { [ticketGroupId]: { $set: nextEntries } },
  });
  if (!pageTicketGroups) return nextForm;

  const ticketGroups = pruneRuleHiddenTickets(nextForm.ticketGroups, pageTicketGroups);
  return ticketGroups === nextForm.ticketGroups ? nextForm : { ...nextForm, ticketGroups };
}
